import { computed, reactive, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { debounce, type DebouncedFunc } from 'lodash';
import { useHipSpineStore } from './hipSpineStore';
import { useUserStore } from '@/stores/userStore';
import { useHipSpineFeatureGate } from '@/stores/featureToggles/useHipSpineFeatureGate';
import { useCupOverlayViewModel } from '@/views/3d-planner/cup/useCupOverlayViewModel';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import { useAdjustmentsMeasurementsViewModel } from '@/views/3d-planner/measurements/useAdjustmentsMeasurementsViewModel';
import {
    computeCombinedVersionTarget,
    type HipSpineSafeZone,
} from '@/domain/anatomy/pelvis/hipSpineNomogramCalculations';
import {
    MissingLandmarksError,
    computeAPPAngle,
    computeAveragePelvicFemoralAngle,
} from '@/domain/anatomy/hipSpineAngleMeasurements';
import {
    classifyHipSpine,
    type HipSpineClassification,
} from '@/domain/anatomy/pelvis/hipSpineClassification';
import {
    computeNomogramData,
    projectSafeZonePolygonAtInclination,
    projectSafeZonePolygonAtAnteversion,
    projectAnteversionAtInclination,
    projectInclinationAtAnteversion,
    type NomogramData,
} from '@/domain/anatomy/pelvis/hipSpineNomogram';
import { lpsIdentityCS } from '@/domain/anatomy/LPS';

const DEBOUNCE_MS = 500;

// eslint-disable-next-line max-lines-per-function -- Pinia setup store: cohesive ViewModel; length is inherent (B-B SOLID signal not applicable to a defineStore body).
export const useHipSpineAnalysisViewModel = defineStore('hip-spine-analysis', () => {
    const store = useHipSpineStore();
    const userStore = useUserStore();
    const cupStore = useCupOverlayViewModel();
    const plannerStore = usePlannerStore();
    const measurements = useAdjustmentsMeasurementsViewModel();

    // --- Composed feature gate (organisation feature AND Amplitude feature toggle) ---
    const { enabled: featureEnabled } = useHipSpineFeatureGate();

    // --- Permissions ---
    const canEditBounds = computed(
        () => userStore.isSurgeon || userStore.isSales || userStore.isAdmin,
    );

    // --- Editing state ---
    const isEditingBounds = ref(false);
    const editableBounds = reactive<HipSpineSafeZone>({
        minAnteversion: 0,
        maxAnteversion: 40,
        minInclination: 20,
        maxInclination: 60,
    });

    // --- Dynamic input constraints (lower and upper must differ by at least 1) ---
    const inclinationLowerMax = computed(() => editableBounds.maxInclination - 1);
    const inclinationUpperMin = computed(() => editableBounds.minInclination + 1);
    const anteversionLowerMax = computed(() => editableBounds.maxAnteversion - 1);
    const anteversionUpperMin = computed(() => editableBounds.minAnteversion + 1);

    // --- Effective bounds (local while editing, store otherwise) ---
    const effectiveBounds = computed<HipSpineSafeZone | null>(() => {
        if (isEditingBounds.value) {
            return { ...editableBounds };
        }
        return store.hipSpineSafeZone;
    });

    // --- Debounced save ---
    let debouncedSave: DebouncedFunc<() => Promise<void>> | null = null;

    function createDebouncedSave(): DebouncedFunc<() => Promise<void>> {
        return debounce(async () => {
            try {
                await store.updateBounds({ ...editableBounds });
            } catch {
                // Revert to store values on failure
                const storeBounds = store.hipSpineSafeZone;
                if (storeBounds) {
                    Object.assign(editableBounds, storeBounds);
                }
            }
        }, DEBOUNCE_MS);
    }

    // --- Editing lifecycle ---
    function startEditing(): void {
        const bounds = store.hipSpineSafeZone;
        if (!bounds) return;
        Object.assign(editableBounds, bounds);
        debouncedSave = createDebouncedSave();
        isEditingBounds.value = true;
    }

    function stopEditing(): void {
        debouncedSave?.flush();
        debouncedSave?.cancel();
        debouncedSave = null;
        isEditingBounds.value = false;
    }

    function clampBound(field: keyof HipSpineSafeZone, value: number): number {
        const limits: Record<keyof HipSpineSafeZone, { min: number; max: number }> = {
            minInclination: { min: 20, max: inclinationLowerMax.value },
            maxInclination: { min: inclinationUpperMin.value, max: 60 },
            minAnteversion: { min: 0, max: anteversionLowerMax.value },
            maxAnteversion: { min: anteversionUpperMin.value, max: 40 },
        };
        const { min, max } = limits[field];
        return Math.max(min, Math.min(max, value));
    }

    function updateBound(field: keyof HipSpineSafeZone, value: number): void {
        editableBounds[field] = clampBound(field, value);
        debouncedSave?.();
    }

    // Reset editing state when the active case or study changes, so stale
    // edits from a previous case don't leak into a new one. Cancels (does
    // not flush) any pending debounced save so it can't fire against the
    // new case's study IDs.
    watch(
        () => [store.caseId, store.studyId],
        () => {
            debouncedSave?.cancel();
            debouncedSave = null;
            isEditingBounds.value = false;
        },
    );

    // --- Nomogram data (migrated from useNomogramData) ---
    const combinedVersionTarget = computed<number | null>(() => {
        const stemAnteversion = measurements.stemAnteversion;
        if (stemAnteversion === null) return null;
        return computeCombinedVersionTarget(stemAnteversion);
    });

    const nomogramData = computed<NomogramData | null>(() => {
        const standingLandmarks = store.approvedLandmarks.find(
            (l) => l.posture === 'standing-sagittal',
        );
        if (!standingLandmarks) return null;

        try {
            const pelvicFemoralAngle = computeAveragePelvicFemoralAngle(standingLandmarks);
            const appAngle = computeAPPAngle(standingLandmarks);
            const targetCS = plannerStore.alignmentCS ?? lpsIdentityCS();
            const appCS = plannerStore.appCS ?? lpsIdentityCS();

            return computeNomogramData({
                targetCS,
                angleRanges: effectiveBounds.value,
                currentAnteversion: cupStore.anteversion ?? null,
                currentInclination: cupStore.inclination ?? null,
                combinedVersionTarget: combinedVersionTarget.value,
                pelvicFemoralAngle,
                appAngle,
                appCS,
            });
        } catch (e) {
            if (e instanceof MissingLandmarksError) return null;
            throw e;
        }
    });

    // --- Delegated store state ---
    const safeZoneVisibility = computed(() => store.safeZoneVisibility);
    const isLoading = computed(() => store.isLoading);

    // --- WI-677 safe-zone slider bindings ---
    // Use nomogramData.marker (derived from cupStore via computeNomogramData) to avoid
    // re-reading cupStore reactivity — marker is null when either cup angle is missing.
    const anteversionSafeBand = computed<{ min: number; max: number } | null>(() => {
        const nomogram = nomogramData.value;
        if (!nomogram) return null;
        const polygon = nomogram.safeZonePolygon ?? null;
        const inclination = nomogram.marker?.inclination ?? null;
        if (inclination == null) return null;
        return projectSafeZonePolygonAtInclination(polygon, inclination);
    });

    const inclinationSafeBand = computed<{ min: number; max: number } | null>(() => {
        const nomogram = nomogramData.value;
        if (!nomogram) return null;
        const polygon = nomogram.safeZonePolygon ?? null;
        const anteversion = nomogram.marker?.anteversion ?? null;
        if (anteversion == null) return null;
        return projectSafeZonePolygonAtAnteversion(polygon, anteversion);
    });

    const combinedVersionAtAnteversionAxis = computed<number | null>(() => {
        const nomogram = nomogramData.value;
        if (!nomogram) return null;
        const line = nomogram.combinedVersionTarget ?? null;
        const inclination = nomogram.marker?.inclination ?? null;
        if (inclination == null) return null;
        return projectAnteversionAtInclination(line, inclination);
    });

    const combinedVersionAtInclinationAxis = computed<number | null>(() => {
        const nomogram = nomogramData.value;
        if (!nomogram) return null;
        const line = nomogram.combinedVersionTarget ?? null;
        const marker = nomogram.marker ?? null;
        if (marker == null) return null;
        return projectInclinationAtAnteversion(line, marker.anteversion, marker.inclination);
    });

    const isCupOutsideSafeZone = computed<boolean>(() => {
        const nomogram = nomogramData.value;
        if (!nomogram) return false;
        return !nomogram.isMarkerInSafeZone;
    });

    // Use effectiveBounds so the grey surgeon band tracks live edits, matching
    // the green safe band (which derives from effectiveBounds via the polygon).
    // Falls back to the persisted store bounds when not editing.
    const surgeonAnteversionBounds = computed<{ min: number; max: number } | null>(() => {
        const zone = effectiveBounds.value;
        return zone ? { min: zone.minAnteversion, max: zone.maxAnteversion } : null;
    });

    const surgeonInclinationBounds = computed<{ min: number; max: number } | null>(() => {
        const zone = effectiveBounds.value;
        return zone ? { min: zone.minInclination, max: zone.maxInclination } : null;
    });

    // --- Hip Spine View toggle (gate + writable v-model target) ---
    // Expose the presentation conditions and the toggle through the ViewModel
    // so the View never reads or mutates hipSpineStore state directly.
    // Gate analysis visibility on the existence of approved snapshots, NOT on the
    // absence of pending changes. The analysis renders the static approved snapshot,
    // so it must persist when a posture is edited+saved (re-approval pending) and only
    // refresh once those changes are approved. Using allPosturesApproved here would
    // hide the whole analysis the instant any edit is saved. WI-687.
    const canShowSafeZoneControls = computed<boolean>(
        () => store.isStudyCompleted && store.allPosturesHaveApprovedLandmarks,
    );

    const isSafeZoneSliderMode = computed<boolean>({
        get: () => store.isSafeZoneSliderMode,
        set: (value) => store.setSafeZoneSliderMode(value),
    });

    const classification = computed<HipSpineClassification | null>(() => {
        const standing = store.approvedLandmarks.find((l) => l.posture === 'standing-sagittal');
        if (!standing) return null;
        const lumbar = store.approvedLandmarks.find((l) => l.posture === 'lumbar-sagittal') ?? null;
        const flexedSeated =
            store.approvedLandmarks.find((l) => l.posture === 'flexed-seated-sagittal') ?? null;

        try {
            return classifyHipSpine({
                standingLandmarks: standing,
                flexedSeatedLandmarks: flexedSeated,
                lumbarLandmarks: lumbar,
            });
        } catch (e) {
            if (e instanceof MissingLandmarksError) return null;
            throw e;
        }
    });

    // Whether the hip-spine is CURRENTLY fully approved (no posture pending re-approval).
    // Drives the review button's label ("View Approved Hip-Spine" vs "Review Hip-Spine")
    // and its check icon — so the button correctly reverts to "Review" once an edit is
    // saved. This is deliberately NOT the snapshot-based gate used for analysis visibility
    // (canShowSafeZoneControls / showWarningsPanel), which must persist across edits.
    const isHipSpineApproved = computed(() => store.allPosturesApproved);

    const showWarningsPanel = computed(
        () => classification.value !== null && store.allPosturesHaveApprovedLandmarks,
    );

    return {
        featureEnabled,
        canEditBounds,
        isEditingBounds,
        editableBounds,
        effectiveBounds,
        inclinationLowerMax,
        inclinationUpperMin,
        anteversionLowerMax,
        anteversionUpperMin,
        startEditing,
        stopEditing,
        updateBound,
        nomogramData,
        safeZoneVisibility,
        isLoading,
        // WI-677 safe-zone slider bindings
        anteversionSafeBand,
        inclinationSafeBand,
        combinedVersionAtAnteversionAxis,
        combinedVersionAtInclinationAxis,
        isCupOutsideSafeZone,
        surgeonAnteversionBounds,
        surgeonInclinationBounds,
        canShowSafeZoneControls,
        isSafeZoneSliderMode,
        classification,
        isHipSpineApproved,
        showWarningsPanel,
    };
});
